// Drugs › All maps (ba-web): the Gallery's "Where drugs meet the cortex" in the app. Every drug the Drug list offers, on its
// primary target, painted on the cortex the viewport draws and kept as a tile: the predicted occupancy at the run's own
// scenario (the Compare table's number, equilibrium site binding) × Hansen 2022's relative receptor density on Schaefer-400
// (the pack's `receptor-<target>` parcel table), the figure's "engagement / relative target density" on one 0–1 scale. A
// drug with no binding modelled (ketamine, propofol) shows its target's density alone and says so, as the figure does.
//
// The viewport paints each map in turn through the live vertex layer (viewport-api-v0) and answers `capture` with the
// frame, from wherever the camera is, so the tiles share the view's framing; the layer comes off once the last tile is in
// and the course's own tint returns. The 2D preview has no setVertexData: the tiles carry the numbers and the sheet says the
// maps need the 3D viewport.

import { create } from 'zustand';
import { useAtlas } from '../app/store.ts';
import { getViewport } from '../viewport/host.ts';
import type { Command } from '../viewport/api.ts';
import { NoMap, readMap, type DensityMap } from './drugDensity.ts';
import { openCompare, useCompare, type CompareRow } from './compare.ts';
import baselines from '../data/pet-baselines.json';

/** The layer's id in the viewport; a second layer beside the Show control's `drugs:density`, newest on top. */
export const MAPS_LAYER = 'drugs:maps';
/** The tiles' one scale: relative density is 0–1 within a target, and engagement is occupancy × density, so at most that. */
export const MAPS_RANGE: [number, number] = [0, 1];
/** The layer's look: the density map's own (viridis, the map's colour alone wherever it has a value), over the shared 0–1. */
export const MAPS_LOOK = { colormap: 'viridis' as const, range: MAPS_RANGE, rise: [-1, 1] as [number, number], albedo: 1 };
/** The frame is captured this wide, at the viewport's own aspect, then cut to the brain's own extent for the tile. */
export const CAPTURE_WIDTH = 1600;

export type TileShows = 'engagement' | 'density' | 'none';
export type TileStatus = 'waiting' | 'painting' | 'done' | 'none' | 'error';

export interface MapTile {
  /** The Drug list's value (the run's path), the run's name and scenario. */
  value: string;
  name: string;
  scenario: string | null;
  /** The primary target, or the first modelled one; null where the run names none. */
  target: string | null;
  /** The occupancy the tile is scaled by (the Compare cell's central), null where no binding is modelled. */
  occupancy: number | null;
  from: 'prediction' | 'course-peak' | null;
  exposureNm: number | null;
  /** The run's own reason when it models no binding. */
  note: string | null;
  shows: TileShows;
  status: TileStatus;
  why: string | null;
  /** The captured frame, an object URL, once painted. */
  url: string | null;
  /** Of the values pushed: vertices with a value, and their mean (probes, and a check that the scaling happened). */
  finite: number | null;
  mean: number | null;
}

/** A row of the Compare table as a tile: its primary target's cell scales the density; no cell, the density alone. */
export function tileOf(row: CompareRow): MapTile {
  const target = row.primary ?? row.cells[0]?.target ?? null;
  const cell = target ? (row.cells.find((c) => c.target === target) ?? null) : null;
  return {
    value: row.value,
    name: row.name,
    scenario: row.scenario,
    target,
    occupancy: cell ? cell.central : null,
    from: cell ? cell.from : null,
    exposureNm: row.exposureNm,
    note: row.note ?? null,
    shows: cell ? 'engagement' : target ? 'density' : 'none',
    status: 'waiting',
    why: null,
    url: null,
    finite: null,
    mean: null,
  };
}

/** Engagement from a density map: occupancy × relative density a vertex; NaN stays NaN. */
export function engagement(values: Float32Array, occupancy: number): Float32Array {
  const out = new Float32Array(values.length);
  for (let i = 0; i < values.length; i++) out[i] = values[i]! * occupancy;
  return out;
}

export interface Box {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** The extent of a frame's drawn pixels (any channel over `floor`, the background's own level plus a step), widened by `margin` of
 *  its size each way: a tile is the brain, not the whole canvas around it. Null when nothing is drawn. */
export function drawnBounds(d: Uint8ClampedArray, w: number, h: number, floor: number, margin = 0.06): Box | null {
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      if (Math.max(d[i]!, d[i + 1]!, d[i + 2]!) > floor) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  const mx = Math.round((x1 - x0 + 1) * margin);
  const my = Math.round((y1 - y0 + 1) * margin);
  x0 = Math.max(0, x0 - mx);
  y0 = Math.max(0, y0 - my);
  x1 = Math.min(w - 1, x1 + mx);
  y1 = Math.min(h - 1, y1 + my);
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** The background's level: the brightest channel among the frame's four corners. */
export function backgroundLevel(d: Uint8ClampedArray, w: number, h: number): number {
  let level = 0;
  for (const [x, y] of [[0, 0], [w - 1, 0], [0, h - 1], [w - 1, h - 1]] as const) {
    const i = (y * w + x) * 4;
    level = Math.max(level, d[i]!, d[i + 1]!, d[i + 2]!);
  }
  return level;
}

/** A captured frame as a tile: cut to `box`, or to the brain's own extent when no box is given yet (the first tile finds it; the
 *  camera holds still while the set paints, so every tile shares it). */
async function toTile(png: Blob, box: Box | null): Promise<{ blob: Blob; box: Box }> {
  const bmp = await createImageBitmap(png);
  const full = new OffscreenCanvas(bmp.width, bmp.height);
  const g = full.getContext('2d')!;
  g.drawImage(bmp, 0, 0);
  bmp.close();
  let b = box;
  if (!b) {
    const d = g.getImageData(0, 0, full.width, full.height).data;
    b = drawnBounds(d, full.width, full.height, backgroundLevel(d, full.width, full.height) + 24) ?? { x: 0, y: 0, w: full.width, h: full.height };
  }
  const out = new OffscreenCanvas(b.w, b.h);
  out.getContext('2d')!.drawImage(full, b.x, b.y, b.w, b.h, 0, 0, b.w, b.h);
  return { blob: await out.convertToBlob({ type: 'image/png' }), box: b };
}

/** Vertices with a value, and their mean. */
export function statsOf(values: ArrayLike<number>): { finite: number; mean: number | null } {
  let n = 0;
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    const v = values[i]!;
    if (Number.isFinite(v)) {
      n++;
      sum += v;
    }
  }
  return { finite: n, mean: n ? sum / n : null };
}

const nm = (v: number) => (v >= 100 ? `${Math.round(v)} nM` : v >= 1 ? `${v.toFixed(1)} nM` : `${v.toFixed(2)} nM`);

/** The tile's two lines: the target with "61 % binding" (or "receptor density"), and the scenario's free brain concentration
 *  (or the run's own note where it models no binding). */
export function tileLines(t: MapTile, label: (code: string) => string = (c) => c): { what: string; how: string } {
  const target = t.target ? label(t.target) : '—';
  const what = t.occupancy !== null ? `${target} · ${Math.round(t.occupancy * 100)} % binding${t.from === 'course-peak' ? ' at the course’s peak' : ''}` : `${target} · receptor density`;
  const how =
    t.occupancy !== null
      ? t.exposureNm !== null
        ? `Free brain ${nm(t.exposureNm)}`
        : (t.scenario ?? '')
      : (t.note ?? (t.target ? 'No binding modelled' : 'No target modelled'));
  return { what, how };
}

/** One audited drug of ba-pharm's retrospective PET baseline audit (`src/data/pet-baselines.json`, written by `scripts/pet-baselines.py`
 *  from `research/2026-10-04-landscape-v2-pharm/results.json`): the frozen occupancy model's mean absolute error against measured
 *  human occupancy, in percentage points, beside each trivial baseline's. The occupancy a tile is scaled by is that model's. */
export interface PetBaseline {
  drug: string;
  study_name: string;
  units: number;
  endpoints: number;
  regions: number;
  model_mae_pp: number;
  baselines_mae_pp: Record<string, number>;
  best_baseline: string;
  best_baseline_text: string;
  best_baseline_mae_pp: number;
  beats_every_trivial_baseline: boolean;
}

const BASELINES_FILE = baselines as unknown as { source: { results: string; note: string; figure: string; model: string }; entries: PetBaseline[] };
export const PET_BASELINES: PetBaseline[] = BASELINES_FILE.entries;
export const PET_AUDIT = BASELINES_FILE.source;

/** The audit's entry for a Drug-list run (`pharma/<drug>`), or null: no measured PET was scored for it. */
export const baselineFor = (value: string): PetBaseline | null => PET_BASELINES.find((e) => e.drug === value.replace(/^pharma\//, '')) ?? null;

const pp = (v: number) => `${v.toFixed(1)} pp`;

/** The tile's measured-PET line: the model's error beside the best trivial baseline's, or that there is nothing to compare.
 *  A model under every trivial baseline has the lowest point MAE in its study, which is not a robust win (PHARM-WB-001's
 *  round-2 review, #1187: the point ordering is qualified, not the number). */
export function petLine(value: string): string {
  const b = baselineFor(value);
  if (!b) return 'No measured PET to compare';
  return b.beats_every_trivial_baseline
    ? `Measured PET: model ${pp(b.model_mae_pp)} off; lowest point MAE in ${b.units} people, not a robust win (best trivial baseline ${pp(b.best_baseline_mae_pp)}, ${b.best_baseline_text})`
    : `Measured PET: model ${pp(b.model_mae_pp)} off, best trivial baseline ${pp(b.best_baseline_mae_pp)} (${b.best_baseline_text})`;
}

const COUNT_WORD = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];

/** The audit's verdict as a sentence, from the data. With one study under every trivial baseline it is the reviewed
 *  sentence of research/2026-10-04-pharma-workbench-replay/showcase-item.json, byte for byte (test/pet-baselines.test.ts
 *  holds it): "Four known PET studies replayed exactly; clozapine's model has the lowest point MAE in its study of 15
 *  people, not a robust win." */
export function auditVerdict(): string {
  const under = PET_BASELINES.filter((b) => b.beats_every_trivial_baseline);
  const n = PET_BASELINES.length;
  const studies = `${COUNT_WORD[n] ?? String(n)} known PET studies replayed exactly;`;
  if (!under.length) return `${studies} no model has the lowest point MAE in its study.`;
  const each = under.map((b) => `${b.study_name.toLowerCase()}'s model has the lowest point MAE in its study of ${b.units} people`);
  return `${studies} ${each.join(', and ')}, not a robust win.`;
}

export type MapsStatus = 'idle' | 'loading' | 'painting' | 'done' | 'none' | 'error';

interface MapsState {
  open: boolean;
  setOpen(open: boolean): void;
  status: MapsStatus;
  /** The Drug list and pack the tiles were painted for, so a new list or pack paints again. */
  key: string | null;
  tiles: MapTile[];
  why: string | null;
  /** The pack painted from, the view's preset at the time and the tiles' pixel size (the brain's extent in the frame), for the sheet. */
  pack: string | null;
  view: string | null;
  size: { width: number; height: number } | null;
  /** True while the layer is on the cortex. */
  layerOn: boolean;
  /** Tiles painted so far. */
  painted: number;
}

export const useDrugMaps = create<MapsState>((set) => ({
  open: false,
  setOpen: (open) => {
    set({ open });
    if (!open) void stopPainting();
  },
  status: 'idle',
  key: null,
  tiles: [],
  why: null,
  pack: null,
  view: null,
  size: null,
  layerOn: false,
  painted: 0,
}));

/** The live API (viewport-api-v0, Live vertex data), optional on today's viewports: the 2D preview has no `setVertexData`. */
interface LiveVp {
  send(cmd: Command): Promise<unknown>;
  setVertexData?(id: string, values: Float32Array, t?: number): void;
}

export const NEEDS_LIVE = 'The 2D preview draws the cortex without the maps: they need the 3D viewport.';
export const NO_PACK = 'This viewport draws no HD cortex over the pack, so there is no mesh to carry the maps.';

const reason = (e: unknown): string => (e instanceof Error ? e.message : String(e));
let gen = 0;

const frames = (n: number) =>
  new Promise<void>((ok) => {
    const step = () => (n-- > 0 ? requestAnimationFrame(step) : ok());
    requestAnimationFrame(step);
  });
const pause = (ms: number) => new Promise<void>((ok) => setTimeout(ok, ms));

function forget(tiles: MapTile[]): void {
  for (const t of tiles) if (t.url) URL.revokeObjectURL(t.url);
}

async function removeLayer(vp: LiveVp | null): Promise<void> {
  useDrugMaps.setState({ layerOn: false });
  try {
    await vp?.send({ cmd: 'vertexLayer', id: MAPS_LAYER, remove: true } as unknown as Command);
  } catch {
    // A viewport that never took the layer has nothing to forget.
  }
}

/** Paint the tiles for the Drug list on the cortex the viewport draws, once per list and pack (a change starts over; a
 *  finished set stays for the next opening). */
export async function paintMaps(options: { value: string; label: string }[], pack: string | null): Promise<void> {
  const key = `${options.map((o) => o.value).join(' ')}|${pack ?? ''}`;
  const st = useDrugMaps.getState();
  if (st.key === key && st.status !== 'idle' && st.status !== 'error') return;
  const my = ++gen;
  forget(st.tiles);
  useDrugMaps.setState({ status: 'loading', key, tiles: [], why: null, pack, view: useAtlas.getState().preset, size: null, painted: 0 });
  await openCompare(options);
  if (my !== gen) return;
  const table = useCompare.getState().table;
  if (!table) {
    useDrugMaps.setState({ status: 'error', why: useCompare.getState().why ?? 'The runs’ headers didn’t load.' });
    return;
  }
  const tiles = table.rows.map(tileOf);
  useDrugMaps.setState({ tiles });
  const vp = (await getViewport()) as LiveVp | null;
  if (my !== gen) return;
  if (!vp || typeof vp.setVertexData !== 'function') {
    useDrugMaps.setState({ status: 'none', why: NEEDS_LIVE });
    return;
  }
  if (!pack) {
    useDrugMaps.setState({ status: 'none', why: NO_PACK });
    return;
  }
  const canvas = document.querySelector<HTMLCanvasElement>('canvas.viewport');
  const aspect = canvas && canvas.clientHeight > 0 && canvas.clientWidth > 0 ? canvas.clientWidth / canvas.clientHeight : 1.6;
  const frame = { width: CAPTURE_WIDTH, height: Math.max(240, Math.round(CAPTURE_WIDTH / aspect)) };
  useDrugMaps.setState({ status: 'painting' });
  let box: Box | null = null;
  const update = (i: number, patch: Partial<MapTile>) => useDrugMaps.setState((s) => ({ tiles: s.tiles.map((t, k) => (k === i ? { ...t, ...patch } : t)) }));
  let layer = false;
  try {
    for (let i = 0; i < tiles.length; i++) {
      const t = tiles[i]!;
      if (t.shows === 'none' || !t.target) {
        update(i, { status: 'none', why: t.note ?? 'No target modelled' });
        continue;
      }
      update(i, { status: 'painting' });
      let map: DensityMap;
      try {
        map = await readMap(t.target, 'density', pack);
      } catch (e) {
        if (my !== gen) return;
        update(i, { status: e instanceof NoMap ? 'none' : 'error', why: e instanceof NoMap ? `The cortex’s pack has no parcel table for ${t.target}.` : reason(e) });
        continue;
      }
      if (my !== gen) return;
      const values = t.shows === 'engagement' && t.occupancy !== null ? engagement(map.values, t.occupancy) : map.values;
      if (!layer) {
        await vp.send({ cmd: 'vertexLayer', id: MAPS_LAYER, surface: 'cortex', space: 'own', kind: 'scalar', look: MAPS_LOOK } as unknown as Command);
        if (my !== gen) return;
        layer = true;
        useDrugMaps.setState({ layerOn: true });
      }
      vp.setVertexData(MAPS_LAYER, values);
      // The engine takes the data on its next frame; the capture draws one after that.
      await frames(2);
      await pause(150);
      if (my !== gen) return;
      const { png } = (await vp.send({ cmd: 'capture', width: frame.width, height: frame.height } as Command)) as { png: Blob };
      if (my !== gen) return;
      const tile = await toTile(png, box);
      if (my !== gen) return;
      if (!box) {
        box = tile.box;
        useDrugMaps.setState({ size: { width: box.w, height: box.h } });
      }
      const { finite, mean } = statsOf(values);
      update(i, { status: 'done', url: URL.createObjectURL(tile.blob), finite, mean });
      useDrugMaps.setState((s) => ({ painted: s.painted + 1 }));
    }
  } catch (e) {
    if (my !== gen) return;
    useDrugMaps.setState({ status: 'error', why: reason(e) });
  } finally {
    if (layer && my === gen) await removeLayer(vp);
  }
  if (my !== gen) return;
  if (useDrugMaps.getState().status === 'painting') useDrugMaps.setState({ status: 'done' });
}

/** Stop a painting in progress (the sheet closed, the scene left): the layer comes off and the set is forgotten so the next
 *  opening paints it whole; a finished set stays. */
export async function stopPainting(): Promise<void> {
  const s = useDrugMaps.getState();
  if (s.status !== 'painting' && s.status !== 'loading' && !s.layerOn) return;
  ++gen;
  forget(s.tiles);
  useDrugMaps.setState({ status: 'idle', key: null, tiles: [], painted: 0 });
  await removeLayer((await getViewport()) as LiveVp | null);
}

// Leaving Drugs closes the sheet and forgets the tiles (a module-level subscription: an effect cleanup would run once at
// mount under StrictMode); the next visit paints them for its own list and pack.
useAtlas.subscribe(
  (s) => s.scenario,
  (scenario) => {
    if (scenario === 'drugs') return;
    const s = useDrugMaps.getState();
    if (s.open) s.setOpen(false);
    if (s.status === 'done' || s.status === 'none' || s.status === 'error') {
      forget(s.tiles);
      useDrugMaps.setState({ status: 'idle', key: null, tiles: [], painted: 0, why: null });
    }
  },
);

// The store for probes and the e2e run, as drugDensity.ts exposes its own.
if (typeof window !== 'undefined') (window as unknown as { __drugMaps?: typeof useDrugMaps }).__drugMaps = useDrugMaps;
